import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Pagination,
  Checkbox,
  Loader,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconBrandTabler
} from "@tabler/icons-react";
import AddParameterModal from "./AddParameterModal";
import DeleteModal from "./DeleteModal";
import classes from "../PCComponentParameterPage/PCComponentParameter.module.css";
import { RowDataPCComponentParameter } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { pcComponentParameter } from "../types/RowDataTypes";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { CustomTable } from "../Table/CustomTable";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import { SearchBar } from "../SearchBar/SearchBar";
import Header from "../static/Header";
import getPCComponentParameterTable from "../../DataDisplay/DataDisplayTable/PCComponentParameterTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  CATEGORY = "Category",
  VALUENAME = "Value Name",
  LABELNAME = "Label Name",
  TYPE = "Type",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.CATEGORY]: "category", 
  [colFilter.VALUENAME]: "",
  [colFilter.LABELNAME]: "",
  [colFilter.TYPE]: "type",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [  
    colFilter.CATEGORY.toLowerCase(),
    colFilter.TYPE.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataPCComponentParameter[] = [
  {
    id: "",
    value: "",
    label: "",
    type: "",
    category: "",
  },
];

export function HomePage() {
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  // const [tableData, setTableData] = useState(initialData);

  const [isAddParameterModalOpen, setAddParameterModalOpen] = useState(false);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataPCComponentParameter | null>(null);

  const handleAddParameter = () => {
    setAddParameterModalOpen(true);
  };

  const handleAddFeedModalClose = () => {
    setAddParameterModalOpen(false);
  };

  const handleAddParameterSuccess = async (newParameterData: RowDataPCComponentParameter) => {
    setIsLoading(true);
    await axiosPrivate
    .post(
        "/api/admin/pc-components/create-param",
        {
        category: newParameterData.category,
        value: newParameterData.value,
        label: newParameterData.label,
        type: newParameterData.type,
        },
        {
        headers: {
            "Content-Type": "multipart/form-data",
        },
        }
    )
        .then((response) => {
          // console.log(response);
          changeTableState(); // Trigger table data refresh
          setIsLoading(false);
          showSuccessAlert("Parameter Successfully Added");
        })
        .catch((error) => {
          // console.log("error " + error);
          setIsLoading(false);
          showErrorAlert("Something Went Wrong");
        });
    };

  const handleDelete = (row: RowDataPCComponentParameter) => {
    setSelectedRowToDelete(row);
    console.log(selectedRowToDelete)
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/pc-components/delete-param", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  // const handleView = (row: RowDataPCComponentParameter) => {
  //   setSelectedRowForView(row);
  //   setViewFeedModalOpen(true);
  // };

  // Sorting
  const [sortBy, setSortBy] = useState<keyof RowDataPCComponentParameter | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsOnPage = useRef(0);

  // Table state change trigger
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [colFilters[colFilter.CATEGORY]];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const setSorting = (field: keyof RowDataPCComponentParameter) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  //For hiding/displaying the search filter options
  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    pcComponentParameter,
    getPCComponentParameterTable().create()
  );

  const [totalRows, setTotalRows] = useState<number>(0);
  const rowData: RowDataPCComponentParameter[] | null = fetchedData.data as RowDataPCComponentParameter[];
//   const isLoading: boolean = fetchedData.isLoading;
  const [rowDataPCComponentParameter, setRowDataPCComponentParameter] = useState<RowDataPCComponentParameter[] | null>(null);

  const startIndex = (currentPage - 1) * itemsPerPage;

  useEffect(() => {
    setRowDataPCComponentParameter(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const rowsToMap = rowDataPCComponentParameter ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.category}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.value}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.label}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.type}</Table.Td>
      <Table.Td className={classes.actionButtons}>
        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage !== currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.homeContainer}>
    {isLoading && (
        <div className={classes.loadingOverlay}>
          <Loader size="xl" />
        </div>
      )}
      <ScrollArea className={classes.parentContainer}>
        <Header title="Home Feed" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button className={classes.addButton} onClick={handleAddParameter}>
              <IconBrandTabler className={classes.iconBrandTabler} />
              Create Parameter
            </Button>
          </div>
        </div>

        <AddParameterModal
          isOpen={isAddParameterModalOpen}
          onClose={handleAddFeedModalClose}
          onAddParameter={handleAddParameterSuccess}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <ScrollArea
          w={
            !(!document.getElementById("nav-bar")) ? window.innerWidth -
              document.getElementById("nav-bar")!!.getBoundingClientRect().width : 1000
          }
        >
          <CustomTable
            colHeaders={Object.values(ColumnHeaderToFilterPair)}
            filters={Object.values(colFilters)}
            reverseSortDirection={reverseSortDirection}
            sortBy={sortBy}
            setSorting={setSorting}
            customclasses={classes}
            Th={Th}
            rowData={rowDataPCComponentParameter}
            rows={rows}
          />
        </ScrollArea>

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default HomePage;
